// @flow
import { getEntries } from 'app/packages/trace-logger/resourceTiming';

const MAX_ATTEMPT_COUNT = 7;
const CHECK_INTERVAL = 2000;

type ResourceTimingMap = { [url: string]: PerformanceResourceTiming };

/*
* We try to get data from window.performance.getEntriesByType for every image. We retry
* MAX_ATTEMPT_COUNT number of times because at the time this event is first fired, the entries
* may not be ready with the data that we need. This will keep track of retries and keep checking
* every 2 seconds until we've exhausted the retries or until we have ended timing for all images.
*
* Note that reaching MAX_ATTEMPT_COUNT will cause the promise to resolve, not reject.
*/
const longPollImageTimings = ({
  imageUrls,
  shouldAbort,
}: {
  imageUrls: Array<string>,
  shouldAbort: () => boolean,
}): Promise<ResourceTimingMap> =>
  new Promise((resolve, reject) => {
    const map: ResourceTimingMap = {};
    let attempt = 0;
    const check = () => {
      attempt += 1;
      if (shouldAbort()) {
        reject();
        return;
      }
      const urlsWithoutResourceTiming = imageUrls.filter((url) => !map[url]);
      if (urlsWithoutResourceTiming.length) {
        const resourceEntires = getEntries();
        urlsWithoutResourceTiming.forEach((url) => {
          const entry = resourceEntires.find((entry) => entry.name.includes(url));
          if (entry) {
            map[url] = entry;
          }
        });
      }
      if (attempt === MAX_ATTEMPT_COUNT || imageUrls.every((url) => (map[url] || {}).responseEnd)) {
        resolve(map);
      } else {
        setTimeout(check, CHECK_INTERVAL);
      }
    };
    setTimeout(check, CHECK_INTERVAL); // wait for CHECK_INTERVAL before first attempt
  });

export default longPollImageTimings;
